import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

const Review = ({ currentProduct }) => {
  const [reviews, setReviews] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ratingValue, setRatingValue] = useState(0);

  const [selectedImages, setSelectedImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);

  // Swal Toast
  const Toast = Swal.mixin({
    toast: true,
    position: "bottom-end",
    timer: 2000,
    showConfirmButton: false,
  });

  const errorStyle = {
    border: "1px solid #e53935",
    backgroundColor: "#ffeaea",
  };

  const API_URL = process.env.REACT_APP_API_URL;

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);

    setSelectedImages((prev) => [...prev, ...files]);

    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviewImages((prev) => [...prev, ...previews]);
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadMultipleToS3 = async (files) => {
    if (!files || files.length === 0) return [];

    const formData = new FormData();

    for (let file of files) {
      formData.append("files", file);
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/upload/multiple`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      return res.data.urls;
    } catch (error) {
      console.error("Multiple upload failed:", error);
      return [];
    }
  };

  useEffect(() => {
    fetchReviews(currentProduct._id);
  }, [currentProduct]);

  // Fetch Reviews
  const fetchReviews = async (productId) => {
    try {
      const res = await axios.get(`${API_URL}/review/${productId}`);
      setReviews(res.data.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  // Calculate rating stats
  useEffect(() => {
    if (reviews.length > 0) {
      const starCounts = [1, 2, 3, 4, 5].map((s) => ({
        stars: s,
        count: reviews.filter((r) => r.ratings === s).length,
      }));
      const total = reviews.length;
      const ratingsData = starCounts.map((r) => ({
        ...r,
        percent: total ? Math.round((r.count / total) * 100) : 0,
      }));
      setRatings(ratingsData.reverse());
    } else {
      const emptyRatings = [1, 2, 3, 4, 5].map((s) => ({
        stars: s,
        count: 0,
        percent: 0,
      }));
      setRatings(emptyRatings.reverse());
    }
  }, [reviews]);

  // Submit Review
  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("authToken");
    if (!token) {
      Toast.fire({
        icon: "info",
        title: "Please login to submit a review.",
      });
      return;
    }

    if (ratingValue === 0) {
      Toast.fire({ icon: "error", title: "Please select rating stars." });
      return;
    }

    if (title.trim().length < 3) {
      Toast.fire({
        icon: "error",
        title: "Title must be at least 3 characters",
      });
      return;
    }

    if (description.trim().length < 10) {
      Toast.fire({
        icon: "error",
        title: "Review content must be 10+ characters",
      });
      return;
    }

    try {
      const productId = currentProduct?._id || currentProduct?.productId;

      let uploadedImageURLs = [];

      // Upload images if selected
      if (selectedImages.length > 0) {
        uploadedImageURLs = await uploadMultipleToS3(selectedImages);
      }

      const res = await axios.post(
        `${API_URL}/review/${productId}`,
        {
          ratings: ratingValue,
          title,
          description,
          images: uploadedImageURLs,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (res.data.status === 201) {
        Toast.fire({
          icon: "success",
          title: "Review submitted successfully!",
        });
        setReviews((prev) => [res.data.data, ...prev]);
        setTitle("");
        setDescription("");
        setRatingValue(0);
        setSelectedImages([]);
        setPreviewImages([]);
      } else {
        Toast.fire({
          icon: "error",
          title: res.data.message || "Error submitting review",
        });
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      Swal.fire({
        icon: "warning",
        title: "Oops!",
        text: "Server error while submitting review.",
      });
    }
  };

  return (
    <section className="product-details py-50">
      <div className="container container-lg">
        <div className="">
          <div className="product-dContent border rounded-24">
            {/* Header */}
            <div className="product-dContent__header border-bottom border-gray-100 flex-between flex-wrap gap-16">
              <ul
                className="nav common-tab nav-pills mb-3"
                id="pills-tab"
                role="tablist"
              >
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link active"
                    id="pills-reviews-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-reviews"
                    type="button"
                    role="tab"
                    aria-controls="pills-reviews"
                    aria-selected="true"
                  >
                    Reviews
                  </button>
                </li>
              </ul>

              <Link
                to="#"
                className="btn bg-color-one rounded-16 flex-align gap-8 text-main-600 hover-bg-main-600 hover-text-white"
              >
                {/* <img
                                    src="assets/images/icon/satisfaction-icon.png"
                                    alt="satisfaction"
                                /> */}
                100% Satisfaction Guaranteed
              </Link>
            </div>

            {/* Review Box */}
            <div className="product-dContent__box">
              <div className="tab-content" id="pills-tabContent">
                <div
                  className="tab-pane fade show active"
                  id="pills-reviews"
                  role="tabpanel"
                  aria-labelledby="pills-reviews-tab"
                  tabIndex={0}
                >
                  <div className="row g-4">
                    {/* Left Side: Reviews List + Write Review */}
                    <div className="col-lg-6">
                      <h6 className="mb-24">Customer Reviews</h6>

                      {reviews.length === 0 ? (
                        <p>No reviews yet. Be the first to review!</p>
                      ) : (
                        reviews.map((review) => (
                          <div
                            key={review._id}
                            className="d-flex align-items-start gap-24 pb-44 border-bottom border-gray-100 mb-44"
                          >
                            <img
                              src={
                                review.image ||
                                "assets/images/thumbs/comment-img1.png"
                              }
                              alt="user"
                              className="w-52 h-52 object-fit-cover rounded-circle flex-shrink-0"
                            />
                            <div className="flex-grow-1">
                              <div className="flex-between align-items-start gap-8">
                                <div>
                                  <h6 className="mb-12 text-md">
                                    {review.username}
                                  </h6>
                                  <div className="flex-align gap-8">
                                    {[...Array(review.ratings)].map((_, i) => (
                                      <span
                                        key={i}
                                        className="text-15 fw-medium text-warning-600 d-flex"
                                      >
                                        <i className="ph-fill ph-star" />
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <span className="text-gray-800 text-xs">
                                  {new Date(
                                    review.createdAt,
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                              <h6 className="mb-14 text-md mt-12">
                                {review.title}
                              </h6>
                              <p className="text-gray-700">
                                {review.description}
                              </p>
                              {/* Review Images Gallery */}
                              {review.images && review.images.length > 0 && (
                                <div className="d-flex gap-12 flex-wrap mt-3">
                                  {review.images.map((imgUrl, idx) => (
                                    <img
                                      key={idx}
                                      src={imgUrl}
                                      alt="review"
                                      className="review-image-thumb"
                                      style={{
                                        width: "90px",
                                        height: "90px",
                                        objectFit: "cover",
                                        borderRadius: "8px",
                                        border: "1px solid #ddd",
                                      }}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}

                      {/* Write a Review */}
                      <div className="mt-56">
                        <h6 className="mb-24">Write a Review</h6>
                        <div className="flex-align gap-8 mb-3">
                          {[1, 2, 3, 4, 5].map((num) => (
                            <span
                              key={num}
                              onClick={() => setRatingValue(num)}
                              style={{ cursor: "pointer" }}
                              className={`text-20 d-flex ${
                                ratingValue >= num
                                  ? "text-warning-600"
                                  : "text-gray-400"
                              }`}
                            >
                              <i className="ph-fill ph-star" />
                            </span>
                          ))}
                        </div>

                        <form onSubmit={handleSubmit}>
                          <div className="mb-12">
                            <label className="text-neutral-600 mb-4">
                              Upload Images
                            </label>
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={handleImageSelect}
                              className="common-input rounded-8"
                            />

                            <div className="d-flex gap-3 flex-wrap mt-1">
                              {previewImages.map((img, index) => (
                                <div
                                  key={index}
                                  style={{ position: "relative" }}
                                >
                                  <img
                                    src={img}
                                    alt="preview"
                                    style={{
                                      width: "80px",
                                      height: "80px",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                      border: "1px solid #ddd",
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeImage(index)}
                                    style={{
                                      position: "absolute",
                                      top: "-6px",
                                      right: "-6px",
                                      background: "red",
                                      color: "#fff",
                                      border: "none",
                                      width: "22px",
                                      height: "22px",
                                      borderRadius: "50%",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                    }}
                                  >
                                    X
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="mb-12">
                            <label
                              htmlFor="title"
                              className="text-neutral-600 mb-8"
                            >
                              Review Title
                            </label>
                            <input
                              type="text"
                              className="common-input rounded-8"
                              id="title"
                              placeholder="Great Product"
                              value={title}
                              onChange={(e) => setTitle(e.target.value)}
                            />
                          </div>

                          <div className="mb-12">
                            <label
                              htmlFor="desc"
                              className="text-neutral-600 mb-8"
                            >
                              Review Content
                            </label>
                            <textarea
                              className="common-input rounded-8"
                              id="desc"
                              placeholder="Write your feedback..."
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                            />
                          </div>

                          <button
                            type="submit"
                            className="btn btn-main rounded-pill mt-15"
                          >
                            Submit Review
                          </button>
                        </form>
                      </div>
                    </div>

                    {/* Right Side: Ratings Summary */}
                    <div className="col-lg-6">
                      <div className="ms-xxl-5">
                        <h6 className="mb-24">Customers Feedback</h6>
                        <div className="d-flex flex-wrap gap-44">
                          <div className="border border-gray-100 rounded-8 px-40 py-52 flex-center flex-column flex-shrink-0 text-center">
                            <h2 className="mb-6 text-main-600">
                              {reviews.length
                                ? (
                                    reviews.reduce((a, b) => a + b.ratings, 0) /
                                    reviews.length
                                  ).toFixed(1)
                                : 0}
                            </h2>
                            <div className="flex-center gap-8">
                              {[...Array(5)].map((_, i) => (
                                <span
                                  key={i}
                                  className="text-15 fw-medium text-warning-600 d-flex"
                                >
                                  <i className="ph-fill ph-star" />
                                </span>
                              ))}
                            </div>
                            <span className="mt-16 text-gray-500">
                              Average Product Rating
                            </span>
                          </div>

                          {/* Rating Breakdown */}
                          <div className="border border-gray-100 rounded-8 px-24 py-40 flex-grow-1">
                            {ratings.map((rating, index) => (
                              <div
                                key={index}
                                className={`flex-align gap-8 ${
                                  index < ratings.length - 1 ? "mb-20" : ""
                                }`}
                              >
                                <span className="text-gray-900 flex-shrink-0">
                                  {rating.stars}
                                </span>
                                <div
                                  className="progress w-100 bg-gray-100 rounded-pill h-8"
                                  role="progressbar"
                                  aria-valuenow={rating.percent}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                >
                                  <div
                                    className="progress-bar bg-main-600 rounded-pill"
                                    style={{ width: `${rating.percent}%` }}
                                  />
                                </div>
                                <span className="text-gray-900 flex-shrink-0">
                                  {rating.count}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* End Right */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Review;
